import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Shield, Calendar, Key, Copy, RefreshCw, X, Bitcoin } from 'lucide-react';
import toast from 'react-hot-toast';

const Account = () => {
  const { user, get2FACode } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [isLoadingKey, setIsLoadingKey] = useState(true);
  const [showFullKey, setShowFullKey] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [pollInterval, setPollInterval] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupPaymentMethod, setTopupPaymentMethod] = useState(null);
  const [login2faMode, setLogin2faMode] = useState('always');
  const [isLoading2faMode, setIsLoading2faMode] = useState(true);
  const [totalSavings, setTotalSavings] = useState(0);
  const [isLoadingSavings, setIsLoadingSavings] = useState(true);

  // Fetch API key and 2FA preference on mount
  React.useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const response = await fetch('/api/api-keys', {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        });
        
        const data = await response.json();
        if (data.success && data.data.master_key) {
          setApiKey(data.data.master_key.api_key);
        }
      } catch (error) {
        console.error('Failed to fetch API key:', error);
      } finally {
        setIsLoadingKey(false);
      }
    };

    const fetch2faMode = async () => {
      try {
        const response = await fetch('/api/user-preferences/2fa-login-mode', {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        });
        
        const data = await response.json();
        if (data.success) {
          setLogin2faMode(data.data.login_2fa_mode);
        }
      } catch (error) {
        console.error('Failed to fetch 2FA mode:', error);
      } finally {
        setIsLoading2faMode(false);
      }
    };

    const fetchSavings = async () => {
      try {
        const response = await fetch('/api/user-stats/savings', {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        });
        
        const data = await response.json();
        if (data.success) {
          setTotalSavings(data.data.total_savings);
        }
      } catch (error) {
        console.error('Failed to fetch savings:', error);
      } finally {
        setIsLoadingSavings(false);
      }
    };
    
    if (user) {
      fetchApiKey();
      fetch2faMode();
      fetchSavings();
    }
  }, [user]);

  const toggle2faMode = async () => {
    const newMode = login2faMode === 'always' ? 'trusted_ips' : 'always';
    
    try {
      const response = await fetch('/api/user-preferences/2fa-login-mode', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ login_2fa_mode: newMode })
      });
      
      const data = await response.json();
      if (data.success) {
        setLogin2faMode(newMode);
        toast.success(
          newMode === 'always' 
            ? '2FA will now be required on every login' 
            : '2FA will only be required for new IP addresses (first 2 logins)'
        );
      } else {
        toast.error(data.message || 'Failed to update 2FA preference');
      }
    } catch (error) {
      console.error('Failed to update 2FA mode:', error);
      toast.error('Failed to update 2FA preference');
    }
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    toast.success('API key copied to clipboard!');
  };

  const regenerateApiKey = async (isFirstGeneration = false) => {
    // Only require 2FA verification for regeneration, not first-time generation
    if (!isFirstGeneration && !verificationCode) {
      toast.error('Please verify in Discord first');
      return;
    }

    setIsRegenerating(true);
    try {
      // Only check Discord verification for regeneration
      if (!isFirstGeneration) {
        const verifyResponse = await fetch('/api/reseller-auth/check-2fa-verified', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          },
          body: JSON.stringify({ discord_id: user.discord_id })
        });

        const verifyResult = await verifyResponse.json();
        
        if (!verifyResult.success || !verifyResult.verified) {
          toast.error('Please run the /verify command in Discord first');
          setIsRegenerating(false);
          return;
        }
      }

      // Generate or regenerate API key
      const response = await fetch('/api/api-keys/master', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setApiKey(data.data.api_key);
        if (isFirstGeneration) {
          toast.success('API key generated successfully!');
        } else {
          toast.success('API key regenerated successfully!');
        }
        setShowFullKey(false);
        setShowConfirmModal(false);
        setVerificationCode('');
        if (pollInterval) {
          clearInterval(pollInterval);
          setPollInterval(null);
        }
      } else {
        toast.error(data.message || 'Failed to generate API key');
      }
    } catch (error) {
      toast.error('Failed to generate API key');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleShowKey = () => {
    if (!showFullKey) {
      setShowConfirmModal(true);
    } else {
      setShowFullKey(false);
    }
  };

  const confirmShowKey = async () => {
    if (!user?.discord_id) {
      toast.error('Discord ID not found. Please log out and log back in.');
      setShowConfirmModal(false);
      return;
    }

    setIsGenerating(true);
    try {
      const result = await get2FACode(user.discord_id);
      if (result.success) {
        setVerificationCode(result.code);
        toast.success('Verification code sent! Run the command in Discord.');
        
        // Poll for code updates every 5 seconds
        const interval = setInterval(async () => {
          try {
            const response = await fetch('/api/reseller-auth/get-current-2fa-code', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`
              },
              body: JSON.stringify({ discord_id: user.discord_id })
            });
            
            const data = await response.json();
            if (data.success && data.code) {
              setVerificationCode(data.code);
            } else {
              // Code expired or verified, stop polling
              clearInterval(interval);
              setPollInterval(null);
            }
          } catch (error) {
            console.error('Failed to poll for code updates:', error);
          }
        }, 10000); // Poll every 10 seconds
        
        setPollInterval(interval);
        
        // Clean up polling after 5 minutes
        setTimeout(() => {
          clearInterval(interval);
          setPollInterval(null);
        }, 5 * 60 * 1000);
      } else {
        toast.error(result.message || 'Failed to generate verification code');
        setShowConfirmModal(false);
      }
    } catch (error) {
      toast.error('Failed to generate verification code');
      setShowConfirmModal(false);
    } finally {
      setIsGenerating(false);
    }
  };

  const verifyAndShowKey = async () => {
    if (!user?.discord_id) {
      toast.error('Discord ID not found');
      return;
    }

    try {
      // Check if the Discord command was actually run
      const response = await fetch('/api/reseller-auth/check-2fa-verified', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ discord_id: user.discord_id })
      });

      const result = await response.json();

      if (result.success && result.verified) {
        toast.success('Key shown successfully!');
        setShowFullKey(true);
        setShowConfirmModal(false);
        setVerificationCode('');
      } else {
        toast.error('Please run the /verify command in Discord first');
      }
    } catch (error) {
      toast.error('Failed to verify. Please try again.');
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-100">Account Settings</h1>
        <p className="text-gray-400 mt-1">Manage your account information and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Information Card */}
        <div className="login-box-container flex">
        <div className="card login-box flex-1">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-100 mb-4">Account Information</h2>
            
            <div className="space-y-4">
              {/* Discord Username */}
              <div className="flex items-start">
                <User className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div className="flex-1">
                  <p className="text-sm text-gray-400">Discord Username</p>
                  <p className="text-base text-gray-100 font-medium">{user.discord_username || 'Not available'}</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div className="flex-1">
                  <p className="text-sm text-gray-400">Email</p>
                  <p className="text-base text-gray-100 font-medium">{user.email || 'Not set'}</p>
                </div>
              </div>

              {/* Role */}
              <div className="flex items-start">
                <Shield className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div className="flex-1">
                  <p className="text-sm text-gray-400">Role</p>
                  <p className="text-base text-gray-100 font-medium capitalize">{user.role}</p>
                </div>
              </div>

              {/* Member Since */}
              <div className="flex items-start">
                <Calendar className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div className="flex-1">
                  <p className="text-sm text-gray-400">Member Since</p>
                  <p className="text-base text-gray-100 font-medium">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Unknown'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>
        {/* Balance Card */}
        <div className="login-box-container flex">
        <div className="card login-box flex-1">
          <div className="p-6" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <h2 className="text-lg font-semibold text-gray-100 mb-4">Account Balance</h2>
            
            <div style={{ backgroundColor: '#0a0a0a', border: '1px solid #4a4a4a', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} className="rounded-lg p-6">
              <div>
                <p className="text-sm text-gray-400 mb-2">Current Balance</p>
                <p className="text-3xl font-bold text-gray-100">${(user.balance || 0).toFixed(2)}</p>
              </div>
              
              <button 
                onClick={() => setShowTopupModal(true)}
                className="w-full text-white font-medium py-2 px-4 rounded-md"
                style={{ backgroundColor: '#3b198e', border: 'none', cursor: 'pointer', transition: 'all 0.2s ease' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2d1270';
                  e.currentTarget.style.transform = 'scaleX(1.01) scaleY(1.03)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3b198e';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                Add Funds
              </button>
            </div>
          </div>
        </div>

        </div>
        {/* Discount Information Card */}
        <div className="login-box-container lg:col-span-2">
        <div className="card login-box">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-100 mb-4">Discount Information</h2>
            
            <div style={{ backgroundColor: '#0a0a0a', border: '1px solid #4a4a4a' }} className="rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Your Discount Rate</p>
                  <p className="text-2xl font-bold text-gray-100 mt-1">{user.discount_percentage !== undefined ? user.discount_percentage : 0}%</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-400">Total Savings</p>
                  <p className="text-2xl font-bold text-green-400 mt-1">
                    {isLoadingSavings ? 'Loading...' : `$${totalSavings.toFixed(2)}`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>
        {/* 2FA Login Preference Card */}
        <div className="login-box-container lg:col-span-2">
        <div className="card login-box">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-100">Login 2FA Preference</h2>
              </div>
            </div>
            
            <div style={{ backgroundColor: '#0a0a0a', border: '1px solid #4a4a4a' }} className="rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-base text-gray-100 font-medium mb-2">Require 2FA on Login</p>
                  <p className="text-sm text-gray-400">
                    {login2faMode === 'always' 
                      ? 'Two-factor authentication is required on every login'
                      : 'Two-factor authentication is only required for new IP addresses (first 2 logins)'}
                  </p>
                </div>
                <button
                  onClick={toggle2faMode}
                  disabled={isLoading2faMode}
                  className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                  style={{
                    backgroundColor: login2faMode === 'always' ? '#3b198e' : '#4a4a4a',
                    opacity: isLoading2faMode ? 0.5 : 1,
                    cursor: isLoading2faMode ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span
                    className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                    style={{
                      transform: login2faMode === 'always' ? 'translateX(1.5rem)' : 'translateX(0.25rem)'
                    }}
                  />
                </button>
              </div>
              <div className="mt-4 pt-4" style={{ borderTop: '1px solid #4a4a4a' }}>
                <p className="text-xs text-gray-500">
                  <strong>Always:</strong> 2FA required on every login<br />
                  <strong>Trusted IPs:</strong> 2FA only required for the first 2 logins from a new IP address. After that, the IP is trusted and 2FA is skipped.
                </p>
              </div>
            </div>
          </div>
        </div>

        </div>
        {/* API Keys Card */}
        <div className="login-box-container lg:col-span-2">
        <div className="card login-box">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Key className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-100">API Keys</h2>
              </div>
              <button
                onClick={() => {
                  if (!apiKey) {
                    // Generate first API key (no 2FA required)
                    regenerateApiKey(true);
                  } else {
                    // Show modal for regeneration (requires 2FA)
                    setShowConfirmModal(true);
                  }
                }}
                disabled={isRegenerating || isLoadingKey}
                className="flex items-center gap-2 px-3 py-2 rounded-md transition-colors"
                style={{ backgroundColor: '#3b198e', color: '#ffffff', opacity: (isRegenerating || isLoadingKey) ? 0.5 : 1, cursor: 'pointer' }}
                onMouseEnter={(e) => !(isRegenerating || isLoadingKey) && (e.currentTarget.style.backgroundColor = '#2d1270')}
                onMouseLeave={(e) => !(isRegenerating || isLoadingKey) && (e.currentTarget.style.backgroundColor = '#3b198e')}
              >
                <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">{isRegenerating ? (apiKey ? 'Regenerating...' : 'Generating...') : (apiKey ? 'Regenerate' : 'Generate')}</span>
              </button>
            </div>
            
            <div style={{ backgroundColor: '#0a0a0a', border: '1px solid #4a4a4a' }} className="rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm text-gray-400 mb-2">Your API Key</p>
                  <div className="flex items-center gap-3">
                    {isLoadingKey ? (
                      <code className="text-sm font-mono text-gray-500 bg-black/50 px-3 py-2 rounded border border-gray-700">
                        Loading...
                      </code>
                    ) : apiKey ? (
                      <>
                        <code className="text-sm font-mono text-gray-100 bg-black/50 px-3 py-2 rounded border border-gray-700">
                          {showFullKey ? apiKey : `${apiKey.substring(0, 20)}...${apiKey.substring(apiKey.length - 4)}`}
                        </code>
                        <button
                          onClick={handleShowKey}
                          className="text-sm text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          {showFullKey ? 'Hide' : 'Show'}
                        </button>
                      </>
                    ) : (
                      <code className="text-sm font-mono text-gray-500 bg-black/50 px-3 py-2 rounded border border-gray-700">
                        No API key generated yet
                      </code>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-700 space-y-2">
                <p className="text-xs text-gray-500">
                  Keep your API key secure and never share it publicly.
                </p>
                <p className="text-xs text-yellow-500/80">
                  ⚠️ Regenerating your API key will invalidate the current one immediately.
                </p>
                <p className="text-xs text-yellow-500/80">
                  ⚠️ Regenerating will also invalidate and generate new API links for every product.
                </p>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={() => {
            setShowConfirmModal(false);
            setVerificationCode('');
            if (pollInterval) {
              clearInterval(pollInterval);
              setPollInterval(null);
            }
          }}
        >
          <div 
            className="card max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#1a1a1a', border: '1px solid #4a4a4a' }}
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg" style={{ backgroundColor: '#3b198e' }}>
                  <Key className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-100">Reveal API Key</h3>
              </div>

              <div className="space-y-3 mb-6">
                <p className="text-sm text-gray-300">
                  You are about to reveal your full API key. Please be aware of the following:
                </p>
                
                <div className="space-y-2 bg-black/30 p-4 rounded-lg border border-red-500/30">
                  <div className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">⚠️</span>
                    <p className="text-sm text-red-500/90 font-medium">
                      Never share or show this key to anyone.
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">⚠️</span>
                    <p className="text-sm text-red-500/90 font-medium">
                      If someone gained access to this key they could drain your balance.
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">⚠️</span>
                    <p className="text-sm text-red-500/90 font-medium">
                      Any losses due to these mistakes are non-refundable.
                    </p>
                  </div>
                </div>
                
                {verificationCode && (
                  <div style={{ backgroundColor: '#3b198e20', border: '1px solid #3b198e' }} className="p-4 rounded-lg">
                    <p className="text-sm text-gray-200 mb-2">
                      To reveal your API key, run this command in Discord:
                    </p>
                    <code className="text-sm font-mono text-white bg-black/50 px-3 py-2 rounded border block" style={{ borderColor: '#3b198e' }}>
                      /verify {verificationCode}
                    </code>
                    <p className="text-xs text-gray-400 mt-2">
                      Waiting for verification...
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setVerificationCode('');
                    if (pollInterval) {
                      clearInterval(pollInterval);
                      setPollInterval(null);
                    }
                  }}
                  className="flex-1 px-4 py-2 rounded-md transition-colors"
                  style={{ backgroundColor: '#2a2a2a', border: '1px solid #4a4a4a', color: '#9ca3af' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                >
                  Cancel
                </button>
                {!verificationCode ? (
                  <button
                    onClick={confirmShowKey}
                    disabled={isGenerating}
                    className="flex-1 px-4 py-2 rounded-md transition-colors font-medium"
                    style={{ backgroundColor: '#3b198e', color: '#ffffff', opacity: isGenerating ? 0.5 : 1 }}
                    onMouseEnter={(e) => !isGenerating && (e.currentTarget.style.backgroundColor = '#2d1270')}
                    onMouseLeave={(e) => !isGenerating && (e.currentTarget.style.backgroundColor = '#3b198e')}
                  >
                    {isGenerating ? 'Generating...' : 'I Understand, Show Key'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={verifyAndShowKey}
                      className="flex-1 px-4 py-2 rounded-md transition-colors font-medium"
                      style={{ backgroundColor: '#2a2a2a', border: '1px solid #4a4a4a', color: '#9ca3af' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                    >
                      Show Key
                    </button>
                    <button
                      onClick={() => regenerateApiKey(false)}
                      disabled={isRegenerating}
                      className="flex-1 px-4 py-2 rounded-md transition-colors font-medium"
                      style={{ backgroundColor: '#3b198e', color: '#ffffff', opacity: isRegenerating ? 0.5 : 1 }}
                      onMouseEnter={(e) => !isRegenerating && (e.currentTarget.style.backgroundColor = '#2d1270')}
                      onMouseLeave={(e) => !isRegenerating && (e.currentTarget.style.backgroundColor = '#3b198e')}
                    >
                      {isRegenerating ? 'Regenerating...' : 'Regenerate Key'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Topup Modal */}
      {showTopupModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            position: 'relative'
          }}>
            <button
              onClick={() => {
                setShowTopupModal(false);
                setTopupAmount('');
                setTopupPaymentMethod(null);
              }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X className="h-6 w-6" />
            </button>

            <h2 style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
              Top Up Balance
            </h2>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Amount (USD)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                placeholder="Enter amount"
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '12px' }}>
                Select Payment Method
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div
                  onClick={() => setTopupPaymentMethod('btc')}
                  style={{
                    backgroundColor: '#1a1a1a',
                    border: topupPaymentMethod === 'btc' ? '2px solid #3b198e' : '1px solid #4a4a4a',
                    borderRadius: '8px',
                    padding: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Bitcoin className="h-8 w-8" style={{ color: '#f7931a' }} />
                  <span style={{ color: '#e5e5e5', fontSize: '16px', fontWeight: '500' }}>Bitcoin</span>
                </div>

                <div
                  onClick={() => setTopupPaymentMethod('ltc')}
                  style={{
                    backgroundColor: '#1a1a1a',
                    border: topupPaymentMethod === 'ltc' ? '2px solid #3b198e' : '1px solid #4a4a4a',
                    borderRadius: '8px',
                    padding: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Bitcoin className="h-8 w-8" style={{ color: '#345d9d' }} />
                  <span style={{ color: '#e5e5e5', fontSize: '16px', fontWeight: '500' }}>Litecoin</span>
                </div>
              </div>
            </div>

            <button
              onClick={async () => {
                if (!topupAmount || parseFloat(topupAmount) <= 0) {
                  toast.error('Please enter a valid amount');
                  return;
                }
                if (!topupPaymentMethod) {
                  toast.error('Please select a payment method');
                  return;
                }

                try {
                  const response = await fetch('/api/btcpay/create-topup-invoice', {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      amount: parseFloat(topupAmount),
                      paymentMethod: topupPaymentMethod
                    })
                  });

                  const data = await response.json();
                  if (data.success) {
                    window.open(data.data.checkoutLink, '_blank');
                    setShowTopupModal(false);
                    setTopupAmount('');
                    setTopupPaymentMethod(null);
                  } else {
                    toast.error(data.message || 'Failed to create payment');
                  }
                } catch (error) {
                  toast.error('Failed to process payment');
                }
              }}
              style={{
                width: '100%',
                backgroundColor: '#3b198e',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '14px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Continue to Payment
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default Account;
